import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/common/Layout';
import { apiService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import type { Template } from '../types';

const ManagementTraining: React.FC = () => {
  const { orgId, userId } = useParams<{ orgId: string; userId: string }>();
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [template, setTemplate] = useState<Template | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTemplate = async () => {
      try {
        setLoading(true);
        
        // Validate route parameters
        const orgIdNum = orgId ? parseInt(orgId, 10) : null;
        const userIdNum = userId ? parseInt(userId, 10) : null;
        
        if (!orgIdNum || isNaN(orgIdNum) || !userIdNum || isNaN(userIdNum)) {
          setError('Invalid organization or user ID');
          return;
        }

        // Fetch organization data to get the management template
        const organizations = await apiService.getAllOrganizations();
        const organization = organizations.find(org => org.id === orgIdNum);
        
        if (!organization) {
          setError('Organization not found');
          return;
        }

        // Get the management template assigned to this organization
        const managementTemplate = organization.templates.find(t => t.template_type === 'management');
        
        if (managementTemplate) {
          const fetchedTemplate = await apiService.getTemplate(managementTemplate.id);
          setTemplate(fetchedTemplate);
          setError(null);
        } else {
          setError('No management template found for your organization');
        }
      } catch (err: any) {
        console.error('Failed to fetch management template:', err);
        
        // Handle specific error cases
        if (err.message.includes('401')) {
          setError('You are not authenticated. Please log in again.');
        } else if (err.message.includes('400')) {
          setError('Your account is not associated with an organization.');
        } else if (err.message.includes('404')) {
          setError('No management template found for your organization.');
        } else {
          setError('Failed to load management template. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchTemplate();
    window.scrollTo(0, 0);
  }, [orgId, userId]);

  // Get template name with fallback
  const templateName = template?.name || "Worksheet - Management Plan Prep Guide";

  const handleViewWorksheet = async () => {
    // Mark user as having viewed management training
    if (userId && user) {
      try {
        await apiService.updateUserTrainingStatus(Number(userId), true);
        // Update the user context with the new training status
        const updatedUser = {
          ...user,
          hasViewedManagementTraining: true
        };
        updateUser(updatedUser);
      } catch (error) {
        console.error('Failed to update training status:', error);
        // Don't block navigation if this fails
      }
    }
    
    navigate(`/organization/${orgId}/user/${userId}/responses`);
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading management template...</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <div className="text-6xl mb-6">⚠️</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Unable to Load Template</h3>
            <p className="text-gray-600 mb-6">{error}</p>
            <div className="flex gap-4 justify-center">
              <button
                onClick={() => navigate('/dashboard')}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                ← Back to Dashboard
              </button>
              <button
                onClick={() => window.location.reload()}
                className="inline-flex items-center px-4 py-2 border border-transparent text-base font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-2 py-8">
        {/* Header Card - matching EditTemplate styling */}
        <div className="bg-white rounded-lg shadow mb-8" style={{ height: '65px' }}>
          <div className="flex items-center justify-between h-full px-6">
            {/* Left Side - Breadcrumb */}
            <div className="flex items-center space-x-2">
              {/* Back arrow and Dashboard link */}
              <button
                onClick={() => navigate('/dashboard')}
                className="text-blue-400 hover:text-blue-600 hover:underline font-medium text-sm flex items-center space-x-1 transition-all duration-200"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                <span>Career Development Worksheets</span>
              </button>
              
              <span className="text-gray-400 text-sm">/</span>
              
              {/* Page Title */}
              <h1 className="text-lg font-bold text-gray-900">{templateName}</h1>
            </div>

            {/* Right Side - getting started */}
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-600">
                <span className="font-medium">Getting Started</span>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Main Content */}
        <div className="text-center mb-1">
          {/* Big Management Emoji */}
          <div className="text-6xl mb-6">📋</div>
          
          {/* Template Name in Double Purple Border */}
          <div className="inline-block p-4 border-2 border-purple-500 rounded-lg mb-8">
            <h1 className="text-2xl font-bold text-gray-900">{templateName}</h1>
          </div>
        </div>

        {/* Welcome Card */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm border border-gray-200 p-8 mb-8"> 
          <p className="text-lg text-gray-700 leading-relaxed mb-8">
            <strong>Welcome!</strong> This worksheet will guide you through developing an effective management plan using our proven 5-step approach.
          </p>
          {/* Background */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-3 flex items-center">
              BACKGROUND
            </h2>
            <p>
              One of the biggest challenges many professionals encounter is knowing how to organize a system of plans and procedures to efficiently and effectively fulfill their own responsibilities or ones which are assigned to people they manage.
            </p>
          </div>

          {/* Our Approach */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-3 flex items-center">
              OUR APPROACH
            </h2>
            <p>
              We will introduce a worksheet which uses our 5-step management plan and questions-first approach to guide you in preparing a plan to either manage yourself or the people who report to you.
            </p>
          </div>

          {/* Objectives */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-3 flex items-center">
              OBJECTIVES OR DESIRED OUTCOMES
            </h2>
            <ul className="space-y-2 ml-10">
              <li className="flex items-start text-gray-700">
                <span className="text-purple-500 text-xl mr-2">•</span>
                <span><strong>Control.</strong> Remove any randomness of whether the responsibilities of you or your team will be completed and instead establish a system of managing from expectations and by exceptions.</span>
              </li>
              <li className="flex items-start text-gray-700">
                <span className="text-purple-500 text-xl mr-2">•</span>
                <span><strong>Confidence.</strong> Instill confidence with reasonable assurance your objectives for a given set of responsibilities will be consistently achieved.</span>
              </li>
              <li className="flex items-start text-gray-700">
                <span className="text-purple-500 text-xl mr-2">•</span>
                <span><strong>Empowerment.</strong> Empower users to develop and implement an efficient and effective plan for fulfillment of responsibilities.</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col space-y-4 max-w-md mx-auto">
          <button
            onClick={handleViewWorksheet}
            className="inline-flex items-center justify-center px-6 py-4 border border-transparent text-base font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors"
          >
            📝 Start Management Worksheet
          </button>
        </div>
      </div>
    </div>
    </Layout>
  );
};

export default ManagementTraining;